import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Pencil, TerminalSquare } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import * as consumosApi from '@/api/consumos.api';
import type { ConsumoBucket, ModelPrice, RangoConsumo } from '@/api/consumos.api';
import { Cargando } from '@/components/common/Cargando';

/**
 * Consumos: cuánto cuesta la IA — hoy/7 días/30 días/desde siempre, por agente, canal y tipo,
 * con tendencia de 14 días. El motor CLI (suscripción Max) no lleva dólares:
 * se cuentan usos, y la "ventana" solo se muestra si el puente la reporta.
 */

const RANGOS = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'semana', label: 'Últimos 7 días' },
  { value: 'quincena', label: 'Últimos 15 días' },
  { value: 'mes', label: 'Últimos 30 días' },
  { value: 'siempre', label: 'Desde siempre' },
] as const;

/** Hoy en la hora del navegador, AAAA-MM-DD (para los topes del selector de fechas). */
const hoyLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

const fecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Nombre visible de cada tipo de uso.
 *
 * La tabla se arma con los tipos que REALMENTE aparecen en los datos, así que
 * un tipo nuevo se muestra solo; lo único que falta acá es su nombre en
 * castellano. Sin la fila, se veria el enum crudo ("COPILOTO").
 */
const TIPO_LABEL: Record<string, string> = {
  ATENCION: 'Atención al cliente',
  ENTRENAMIENTO: 'Entrenamiento',
  INTERNO: 'Interno',
  // B6 · Lo que gasta el asistente INTERNO, separado de lo que cuesta atender
  // clientes. Son dos preguntas distintas -- "¿cuánto me cuesta vender?" y
  // "¿cuánto me cuesta mi asistente?"-- y mezcladas no se puede contestar
  // ninguna de las dos.
  COPILOTO: 'Asistente interno',
};

function usd(n: number): string {
  return `US$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function DesgloseTabla<T extends ConsumoBucket>({
  titulo,
  rows,
  nombre,
}: {
  titulo: string;
  rows: T[];
  nombre: (r: T) => string;
}) {
  return (
    <Card>
      <CardBody className="space-y-2">
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{titulo}</p>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400">Sin consumo en este período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400">
                  <th className="py-1 pr-2 font-medium"> </th>
                  <th className="py-1 pr-2 font-medium">Turnos API</th>
                  <th className="py-1 pr-2 font-medium">Usos CLI</th>
                  <th className="py-1 text-right font-medium">Costo est.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-surface-border dark:border-slate-800">
                    <td className="max-w-[180px] truncate py-1.5 pr-2 text-slate-700 dark:text-slate-200">
                      {nombre(r)}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-500">{r.turnosApi}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{r.usosCli || '—'}</td>
                    <td className="py-1.5 text-right font-medium text-slate-800 dark:text-slate-100">
                      {r.usd > 0 ? usd(r.usd) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function PreciosModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const precios = useQuery({
    queryKey: ['consumos', 'precios'],
    queryFn: consumosApi.getPrices,
    enabled: open,
  });
  const [draft, setDraft] = useState<Record<string, ModelPrice> | null>(null);
  const tabla = draft ?? precios.data?.precios ?? {};

  const save = useMutation({
    mutationFn: () => consumosApi.putPrices(tabla),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['consumos'] });
      toast.success('Precios guardados (el histórico se recalcula solo)');
      onClose();
    },
    onError: () => toast.error('No se pudieron guardar los precios'),
  });

  const setCampo = (modelo: string, campo: keyof ModelPrice, valor: number) => {
    setDraft({ ...tabla, [modelo]: { ...tabla[modelo]!, [campo]: valor } });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Precios por modelo"
      description="USD por millón de tokens. El costo mostrado siempre se calcula con la tabla vigente."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      {precios.isLoading ? (
        <Spinner size={24} />
      ) : (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          <div className="grid grid-cols-5 gap-2 text-xs font-medium text-slate-400">
            <span>Modelo</span>
            <span>Entrada</span>
            <span>Salida</span>
            <span>Caché escritura</span>
            <span>Caché lectura</span>
          </div>
          {Object.entries(tabla).map(([modelo, p]) => (
            <div key={modelo} className="grid grid-cols-5 items-center gap-2">
              <span className="truncate text-xs text-slate-600 dark:text-slate-300" title={modelo}>
                {modelo}
              </span>
              {(
                [
                  'inUsdPorMTok',
                  'outUsdPorMTok',
                  'cacheWriteUsdPorMTok',
                  'cacheReadUsdPorMTok',
                ] as const
              ).map((campo) => (
                <input
                  key={campo}
                  type="number"
                  min={0}
                  step={0.01}
                  value={p[campo]}
                  onChange={(ev) => setCampo(modelo, campo, Number(ev.target.value))}
                  className="w-full rounded-md border border-surface-border bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function Consumos() {
  const [range, setRange] = useState<RangoConsumo>('mes');
  const [dias, setDias] = useState<{ desde: string; hasta: string }>({ desde: '', hasta: '' });
  const [preciosOpen, setPreciosOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['consumos', 'summary', range, range === 'rango' ? dias : null],
    queryFn: () => consumosApi.getSummary(range, dias),
  });
  const ponerDia = (campo: 'desde' | 'hasta', valor: string) => {
    setDias((d) => ({ ...d, [campo]: valor }));
    setRange('rango');
  };
  // Si se eligen al revés, el servidor las da vuelta: aquí se dicen igual.
  const [d1, d2] = dias.desde && dias.hasta && dias.hasta < dias.desde ? [dias.hasta, dias.desde] : [dias.desde, dias.hasta];
  const cliWindow = useQuery({
    queryKey: ['consumos', 'cli-window'],
    queryFn: consumosApi.getCliWindow,
  });

  const maxDia = Math.max(0.01, ...(data?.tendencia ?? []).map((d) => d.usd));

  /**
   * CUÁNTOS DÍAS SEGUIDOS LLEVA EN CERO, CONTANDO DESDE HOY HACIA ATRÁS.
   *
   * El 21-oct-2026 OSITO llevaba TRES DÍAS sin una sola llamada a la IA —una
   * clave de Anthropic mal creada devolvía 400 en todas— y esta pantalla no lo
   * decía: tres barras en cero se ven igual que tres barras de gasto chiquito,
   * porque el mínimo de 4% las dibuja todas iguales. Los clientes se llevaron
   * 107 frases de cortesía ese día y nadie se enteró mirando acá.
   */
  const diasEnCero = ((): number => {
    const t = data?.tendencia ?? [];
    let n = 0;
    for (let i = t.length - 1; i >= 0; i -= 1) {
      const d = t[i];
      if (!d || d.usd > 0 || d.turnosApi > 0) break;
      n += 1;
    }
    return n;
  })();
  const totalCli = data?.total.usosCli ?? 0;
  const reporteCli =
    cliWindow.data?.reporte && typeof cliWindow.data.reporte === 'object'
      ? JSON.stringify(cliWindow.data.reporte)
      : null;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Consumos"
        description="Cuánto cuesta la IA: gasto estimado por período, agente, canal y tipo de uso."
        action={
          <Button variant="outline" onClick={() => setPreciosOpen(true)}>
            <Pencil className="h-4 w-4" /> Precios
          </Button>
        }
      />

      {/* Las cuatro cifras de un vistazo; tocar una cambia el desglose de abajo a ese período. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" role="tablist" aria-label="Período">
        {RANGOS.map((r) => {
          const t = data?.totales?.[r.value];
          const desdeSiempre = r.value === 'siempre' ? data?.totales?.siempre.desde : null;
          return (
            <button
              key={r.value}
              type="button"
              role="tab"
              aria-selected={range === r.value}
              onClick={() => setRange(r.value)}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors',
                range === r.value
                  ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/30'
                  : 'border-surface-border hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800',
              )}
            >
              <p className="text-sm text-slate-500 dark:text-slate-400">{r.label}</p>
              <p className="text-xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
                {t ? usd(t.usd) : '—'}
              </p>
              <p className="text-xs text-slate-400">
                {t ? `${t.turnosApi.toLocaleString()} ${t.turnosApi === 1 ? 'turno' : 'turnos'}${t.usosCli ? ` · ${t.usosCli} CLI` : ''}` : ' '}
                {/* La fecha nunca se parte en dos renglones. */}
                {desdeSiempre ? <> · desde el <span className="whitespace-nowrap">{fecha(desdeSiempre)}</span></> : ''}
              </p>
            </button>
          );
        })}
      </div>

      {/* O el rango que uno quiera: del día «desde» al día «hasta», los dos incluidos. */}
      <div
        className={cn(
          'flex flex-wrap items-end gap-3 rounded-lg border p-3',
          range === 'rango'
            ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/30'
            : 'border-surface-border dark:border-slate-700',
        )}
      >
        <p className="w-full text-sm text-slate-500 dark:text-slate-400 sm:w-auto sm:self-center">Elegir fechas</p>
        {(['desde', 'hasta'] as const).map((campo) => (
          <label key={campo} className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            {campo === 'desde' ? 'Desde' : 'Hasta'}
            <input
              type="date"
              value={dias[campo]}
              max={hoyLocal()}
              onChange={(ev) => ponerDia(campo, ev.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </label>
        ))}
        {range === 'rango' && data?.range === 'rango' ? (
          <p className="text-sm text-slate-600 dark:text-slate-300 sm:self-center">
            {d1 ? `Del ${fecha(`${d1}T12:00:00`)}` : 'Desde el principio'}
            {d2 ? ` al ${fecha(`${d2}T12:00:00`)}` : ' hasta hoy'}: <b>{usd(data.total.usd)}</b>
          </p>
        ) : null}
      </div>

      {isLoading || !data ? (
        <Cargando tamano="pantalla" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card>
              <CardBody className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                  <Coins className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Gasto estimado (motor API)
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {usd(data.total.usd)}
                  </p>
                  <p className="text-xs text-slate-400">
                    {data.total.turnosApi} turnos ·{' '}
                    {(data.total.inputTokens + data.total.outputTokens).toLocaleString()} tokens
                  </p>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                  <TerminalSquare className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Motor CLI / Max (suscripción, sin dólares)
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {totalCli} usos
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {reporteCli
                      ? `Ventana reportada: ${reporteCli}`
                      : 'El puente no reporta límites de ventana.'}
                  </p>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Tendencia (14 días)
                  </p>
                  <span className="text-[11px] text-slate-400">máx {usd(maxDia)}/día</span>
                </div>
                <div className="flex h-16 items-end gap-1">
                  {data.tendencia.map((d) => (
                    <div
                      key={d.dia}
                      className="flex h-full flex-1 items-end"
                      title={`${d.dia}: ${usd(d.usd)} · ${d.turnosApi} turnos${d.usosCli ? ` · ${d.usosCli} CLI` : ''}`}
                    >
                      {/*
                        UN DÍA EN CERO TIENE QUE VERSE EN CERO.
                        Con el mínimo de 4% para todos, un día sin UNA sola
                        llamada se dibujaba igual que un día de gasto bajo. Así
                        es como pasaron tres días con la IA caída sin que esta
                        pantalla lo gritara.
                      */}
                      {d.usd > 0 || d.turnosApi > 0 ? (
                        <div
                          className="w-full rounded-t bg-brand-400 dark:bg-brand-600"
                          style={{ height: `${Math.max(4, (d.usd / maxDia) * 100)}%` }}
                        />
                      ) : (
                        <div className="h-[6px] w-full rounded-t border border-dashed border-slate-300 dark:border-slate-600" />
                      )}
                    </div>
                  ))}
                </div>
                {/* Sin fechas, el gráfico no dice CUÁNDO pasó nada. */}
                <div className="flex gap-1">
                  {data.tendencia.map((d, i) => (
                    <span
                      key={d.dia}
                      className="flex-1 text-center text-[9px] leading-none text-slate-400"
                    >
                      {i % 2 === 0 ? d.dia.slice(-2) : ''}
                    </span>
                  ))}
                </div>
                {diasEnCero > 0 ? (
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                    Sin una sola llamada a la IA{' '}
                    {diasEnCero === 1 ? 'hoy' : `en ${diasEnCero} días seguidos`}. Si la asistente
                    debería estar contestando, lo primero que hay que mirar es la clave de IA.
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    Pasa el mouse por una barra para el detalle.
                  </p>
                )}
              </CardBody>
            </Card>
          </div>

          {data.total.modelosSinPrecio.length > 0 && (
            <Card>
              <CardBody className="text-sm text-amber-700 dark:text-amber-300">
                Modelos sin precio configurado (su gasto NO está en el total):{' '}
                {data.total.modelosSinPrecio.join(', ')}.{' '}
                <button className="underline" onClick={() => setPreciosOpen(true)}>
                  Configurar precios
                </button>
              </CardBody>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <DesgloseTabla titulo="Por agente" rows={data.porAgente} nombre={(r) => r.nombre} />
            <DesgloseTabla titulo="Por canal" rows={data.porCanal} nombre={(r) => r.nombre} />
            <DesgloseTabla
              titulo="Por tipo de uso"
              rows={data.porTipo}
              nombre={(r) => TIPO_LABEL[r.kind] ?? r.kind}
            />
          </div>

          <p className="text-xs text-slate-400">
            <Badge variant="default">Nota</Badge> El costo es un estimado: tokens registrados ×
            tabla de precios editable. Los turnos del motor CLI van por la suscripción Max y no
            suman dólares.
          </p>
        </>
      )}

      <PreciosModal open={preciosOpen} onClose={() => setPreciosOpen(false)} />
    </div>
  );
}
